import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpRequest,
} from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  Injector,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';

import { ConnectivityService } from '../connectivity.service';
import { DatabaseService } from '../database.service';
import { OutboxEntry, OutboxMethod } from '../models/offline-http.model';
import { newUuid } from '../repositories/base.repository';
import { SyncApiService } from '../sync/sync-api.service';
import { backoffDelayMs } from '../sync/sync-queue.repository';
import { SyncService } from '../sync/sync.service';
import { apiBase, replayableHeaders } from './offline-endpoints';

/** Shape of an item in the retired localStorage queue (OfflineQueueService). */
interface LegacyQueuedSave {
  payload: unknown;
  isBulk?: boolean;
  timestamp?: number;
}

type SendOutcome = 'delivered' | 'rejected' | 'deferred';

/**
 * Writes made while the API was unreachable, and their delivery.
 *
 * Each entry is the request a screen would have sent, replayed later against
 * the same endpoint. That is deliberate: the business endpoints do more than
 * write a row - they check the interval is still open for editing, create the
 * attempt, recompute progress and section scores - and replaying the real
 * request is the only way an offline save ends up exactly like an online one.
 *
 * Three rules hold throughout:
 *   - Oldest first, and later writes wait behind an earlier one that is backing
 *     off. A teacher's answers must land before the "advance to the next
 *     interval" they recorded after them.
 *   - Every replay carries the entry's Idempotency-Key, so a write whose
 *     response was lost is recognised by the server rather than applied twice.
 *   - Nothing is ever dropped automatically. A refusal is set aside as
 *     `rejected` for a person to see.
 */
@Injectable({ providedIn: 'root' })
export class OutboxService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browser = isPlatformBrowser(this.platformId);
  private readonly databaseService = inject(DatabaseService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly session = inject(SyncApiService);
  private readonly injector = inject(Injector);

  /**
   * Straight to the backend, past every interceptor. A replay is background
   * work: it must not be re-queued by the offline interceptor, and a refusal
   * must not pop a toast or redirect a teacher who is busy on another screen.
   */
  private readonly http = new HttpClient(inject(HttpBackend));

  static readonly LEGACY_QUEUE_KEY = 'sam5_offline_save_queue';
  /** Safety net for a missed reconnect event. */
  private static readonly REPLAY_INTERVAL_MS = 60_000;
  private static readonly REQUEST_TIMEOUT_MS = 30_000;

  private readonly pendingSignal = signal(0);
  private readonly rejectedSignal = signal(0);
  private readonly replayingSignal = signal(false);

  /** Writes stored on this device and not yet delivered. */
  readonly pendingCount = this.pendingSignal.asReadonly();
  /** Writes the server refused, waiting for a person. */
  readonly rejectedCount = this.rejectedSignal.asReadonly();
  readonly isReplaying = this.replayingSignal.asReadonly();
  readonly hasUnsentWork = computed(() => this.pendingSignal() > 0);

  private started = false;
  private running = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Idempotent; called from APP_INITIALIZER. */
  async start(): Promise<void> {
    if (this.started || !this.usable()) return;
    this.started = true;

    await this.recoverStuck();
    await this.refreshCounts();

    this.connectivity.reconnected$.subscribe(() => void this.replay());
    this.timer = setInterval(() => void this.replay(), OutboxService.REPLAY_INTERVAL_MS);
    void this.replay();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.started = false;
  }

  /**
   * Store a write for later.
   *
   * Throws when it cannot be stored - no session, or IndexedDB refused it - so
   * the caller reports the original failure instead of claiming it was saved.
   */
  async enqueue(req: HttpRequest<unknown>, idempotencyKey: string): Promise<OutboxEntry> {
    if (!this.usable()) throw new Error('The offline store is not available.');
    const userKey = this.session.currentUserKey();
    if (!userKey) throw new Error('No signed-in user to store this write for.');

    const now = Date.now();
    const entry: OutboxEntry = {
      idempotencyKey,
      userKey,
      method: req.method as OutboxMethod,
      url: req.urlWithParams,
      body: req.body ?? null,
      headers: replayableHeaders(req.headers),
      status: 'pending',
      attempts: 0,
      nextAttemptAt: now,
      lastStatus: null,
      lastError: null,
      createdAt: now,
      updatedAt: now,
    };

    const db = await this.databaseService.ready();
    const id = await db.outbox.add(entry);
    await this.refreshCounts();
    return { ...entry, id };
  }

  /**
   * Deliver everything that is due, oldest first.
   *
   * Safe to call freely - on reconnect, on a timer, from a button. It returns
   * quietly when already running, signed out, or offline.
   */
  async replay(): Promise<void> {
    if (this.running || !this.usable()) return;
    const userKey = this.session.currentUserKey();
    if (!userKey || !this.session.bearerToken()) return;

    this.running = true;
    let delivered = 0;
    try {
      await this.migrateLegacyQueue(userKey);
      if (!(await this.nextDue(userKey))) return;
      if (!this.connectivity.isOnline() && !(await this.connectivity.check())) return;

      this.replayingSignal.set(true);
      for (;;) {
        const entry = await this.nextDue(userKey);
        if (!entry) break;

        const outcome = await this.send(entry);
        if (outcome === 'delivered') {
          delivered++;
          continue;
        }
        // A refusal is final for that entry and says nothing about the next.
        if (outcome === 'rejected') continue;
        // Deferred: the connection or the server is struggling. Stop, keeping
        // the order, and let the next trigger pick it up.
        break;
      }
    } catch (error) {
      console.error('[offline] outbox replay stopped', error);
    } finally {
      this.running = false;
      this.replayingSignal.set(false);
      await this.refreshCounts();
      // What the server wrote - scores, progress - comes back down into the
      // local tables.
      if (delivered > 0) this.injector.get(SyncService).requestBackgroundSync();
    }
  }

  /** Writes the server refused, for display. */
  async listRejected(): Promise<OutboxEntry[]> {
    const userKey = this.session.currentUserKey();
    if (!userKey || !this.usable()) return [];
    const db = await this.databaseService.ready();
    return db.outbox
      .where('[userKey+status]')
      .equals([userKey, 'rejected'])
      .sortBy('createdAt');
  }

  /** Put a refused write back in line, e.g. after the interval was reopened. */
  async retry(id: number): Promise<void> {
    const db = await this.databaseService.ready();
    await db.outbox.update(id, {
      status: 'pending',
      attempts: 0,
      nextAttemptAt: Date.now(),
      lastError: null,
      updatedAt: Date.now(),
    });
    await this.refreshCounts();
    void this.replay();
  }

  /** Remove a write for good. Only ever from an explicit user decision. */
  async discard(id: number): Promise<void> {
    const db = await this.databaseService.ready();
    await db.outbox.delete(id);
    await this.refreshCounts();
  }

  async refreshCounts(): Promise<void> {
    const userKey = this.session.currentUserKey();
    if (!userKey || !this.usable()) {
      this.pendingSignal.set(0);
      this.rejectedSignal.set(0);
      return;
    }
    try {
      const db = await this.databaseService.ready();
      const count = (status: OutboxEntry['status']) =>
        db.outbox.where('[userKey+status]').equals([userKey, status]).count();
      const [pending, sending, rejected] = await Promise.all([
        count('pending'),
        count('sending'),
        count('rejected'),
      ]);
      this.pendingSignal.set(pending + sending);
      this.rejectedSignal.set(rejected);
    } catch {
      // Counts are informational; a failed read must not break a save.
    }
  }

  // ── Internals ────────────────────────────────────────────────────────────

  /** The oldest unsent write, if it is due. The line waits behind a backoff. */
  private async nextDue(userKey: string): Promise<OutboxEntry | undefined> {
    const db = await this.databaseService.ready();
    const pending = await db.outbox
      .where('[userKey+status]')
      .equals([userKey, 'pending'])
      .sortBy('createdAt');
    const oldest = pending[0];
    return oldest && oldest.nextAttemptAt <= Date.now() ? oldest : undefined;
  }

  private async send(entry: OutboxEntry): Promise<SendOutcome> {
    const db = await this.databaseService.ready();
    const id = entry.id as number;
    await db.outbox.update(id, { status: 'sending', updatedAt: Date.now() });

    try {
      await firstValueFrom(
        this.http
          .request(entry.method, entry.url, {
            body: entry.body,
            headers: this.headersFor(entry),
            responseType: 'json',
          })
          .pipe(timeout(OutboxService.REQUEST_TIMEOUT_MS)),
      );
      await db.outbox.delete(id);
      return 'delivered';
    } catch (error) {
      const status = error instanceof HttpErrorResponse ? error.status : 0;
      const message = describeError(error).slice(0, 500);

      if (status === 0 || status === 401) {
        // The connection dropped, or the session ended. Neither is this
        // write's fault, so it goes back untouched rather than into backoff.
        await db.outbox.update(id, {
          status: 'pending',
          lastStatus: status || null,
          lastError: message,
          updatedAt: Date.now(),
        });
        if (status === 0) void this.connectivity.check();
        return 'deferred';
      }

      if (isTransientStatus(status)) {
        const attempts = entry.attempts + 1;
        await db.outbox.update(id, {
          status: 'pending',
          attempts,
          nextAttemptAt: Date.now() + backoffDelayMs(attempts),
          lastStatus: status,
          lastError: message,
          updatedAt: Date.now(),
        });
        return 'deferred';
      }

      // Understood and refused - the interval closed, the student was
      // unassigned. Retrying cannot change the answer.
      await db.outbox.update(id, {
        status: 'rejected',
        lastStatus: status,
        lastError: message,
        updatedAt: Date.now(),
      });
      return 'rejected';
    }
  }

  private headersFor(entry: OutboxEntry): HttpHeaders {
    let headers = new HttpHeaders(entry.headers);
    // The token is read now, not stored with the entry: it may have been
    // refreshed since, and a credential has no business sitting in IndexedDB.
    const token = this.session.bearerToken();
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    return headers.set('Idempotency-Key', entry.idempotencyKey);
  }

  /** A tab closed mid-send leaves entries in `sending`; the key makes a resend safe. */
  private async recoverStuck(): Promise<void> {
    try {
      const db = await this.databaseService.ready();
      await db.outbox
        .where('status')
        .equals('sending')
        .modify({ status: 'pending', updatedAt: Date.now() });
    } catch {
      // Nothing stuck, or nothing readable; replay will find out either way.
    }
  }

  /**
   * Move saves left in the retired localStorage queue into the outbox, once.
   *
   * That queue recorded no owner. Its saves are attributed to whoever is signed
   * in when it is found, which on the device that made them is the teacher who
   * made them.
   */
  private async migrateLegacyQueue(userKey: string): Promise<void> {
    if (!this.browser) return;

    let raw: string | null;
    try {
      raw = localStorage.getItem(OutboxService.LEGACY_QUEUE_KEY);
    } catch {
      return;
    }
    if (!raw) return;

    let items: LegacyQueuedSave[] = [];
    try {
      const parsed: unknown = JSON.parse(raw);
      items = Array.isArray(parsed) ? (parsed as LegacyQueuedSave[]) : [];
    } catch {
      items = [];
    }

    const base = apiBase();
    const db = await this.databaseService.ready();
    await db.transaction('rw', db.outbox, async () => {
      for (const item of items) {
        if (!item || typeof item.payload !== 'object' || item.payload === null) continue;
        const createdAt = typeof item.timestamp === 'number' ? item.timestamp : Date.now();
        const endpoint = item.isBulk ? 'save-bulk-responses' : 'save-response';
        await db.outbox.add({
          idempotencyKey: newUuid(),
          userKey,
          method: 'POST',
          url: `${base}/api/v1/programs/assessments/teacher-assessment/${endpoint}`,
          body: item.payload,
          headers: { 'Content-Type': 'application/json' },
          status: 'pending',
          attempts: 0,
          nextAttemptAt: createdAt,
          lastStatus: null,
          lastError: null,
          createdAt,
          updatedAt: Date.now(),
        });
      }
    });
    localStorage.removeItem(OutboxService.LEGACY_QUEUE_KEY);
  }

  private usable(): boolean {
    return (
      this.browser && this.databaseService.isAvailable && !this.databaseService.fatalError
    );
  }
}

/**
 * Server outcomes worth trying again later. 409 is the idempotency layer saying
 * the same write is still in flight; anything else in 4xx is a refusal.
 */
function isTransientStatus(status: number): boolean {
  return (
    status === 408 || status === 409 || status === 425 || status === 429 || status >= 500
  );
}

function describeError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    const serverMessage = (error.error as { message?: unknown } | null)?.message;
    return typeof serverMessage === 'string' ? serverMessage : error.message;
  }
  return error instanceof Error ? error.message : String(error);
}
